import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

export default function LdapSettingsPage({ showToast }) {
  const { request, loading } = useApi();
  const navigate = useNavigate();

  const [config, setConfig] = useState({
    ldap_url: '',
    ldap_port: '389',
    ldap_base_dn: '',
    ldap_bind_dn: '',
    ldap_bind_password: '',
    ldap_user_filter: '(uid={{username}})',
    ldap_enabled: 'false'
  });

  const [testLogin, setTestLogin] = useState({ username: '', password: '' });
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingLogin, setTestingLogin] = useState(false);

  useEffect(() => {
    request('/api/admin/ldap-config')
      .then(data => {
        if (data) setConfig(prev => ({ ...prev, ...data }));
      })
      .catch(err => showToast(err.message, 'error'));
  }, [request, showToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'true' : 'false') : value
    }));
  };

  const handleSave = async () => {
    try {
      await request('/api/admin/ldap-config', {
        method: 'POST',
        body: JSON.stringify(config)
      });
      showToast('LDAP settings saved successfully', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await request('/api/admin/ldap-test-connection', {
        method: 'POST',
        body: JSON.stringify({ ldap_url: config.ldap_url })
      });
      showToast(res.message || 'Connection successful', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTestLogin = async () => {
    if (!testLogin.username || !testLogin.password) {
      return showToast('Enter test username and password', 'warning');
    }
    setTestingLogin(true);
    try {
      const res = await request('/api/admin/ldap-test-login', {
        method: 'POST',
        body: JSON.stringify(testLogin)
      });
      showToast(`Login success: ${res.user.name} (${res.user.email})`, 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setTestingLogin(false);
    }
  };

  return (
    <div className="ldap-settings-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>LDAP Configuration</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
           <button onClick={() => navigate('/admin')} className="nav-icon-btn" title="Go Back">←</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
        <div className="section-card">
          <h3 className="mb-6 font-semibold text-lg text-blue">Standard Settings</h3>
          <div className="space-y-6">
            <div className="form-group">
              <label>LDAP Server URL</label>
              <input 
                name="ldap_url" 
                value={config.ldap_url} 
                onChange={handleChange} 
                className="input" 
                placeholder="ldap://ldap.company.com" 
              />
            </div>
            <div className="form-group">
              <label>LDAP Port</label>
              <input 
                name="ldap_port" 
                value={config.ldap_port} 
                onChange={handleChange} 
                className="input" 
                placeholder="389" 
              />
            </div>
            <div className="form-group">
              <label>Base DN</label>
              <input 
                name="ldap_base_dn" 
                value={config.ldap_base_dn} 
                onChange={handleChange} 
                className="input" 
                placeholder="dc=company,dc=com" 
              />
            </div>
            <div className="form-group">
              <label>Bind DN</label>
              <input 
                name="ldap_bind_dn" 
                value={config.ldap_bind_dn} 
                onChange={handleChange} 
                className="input" 
                placeholder="cn=admin,dc=company,dc=com" 
              />
            </div>
            <div className="form-group">
              <label>Bind Password</label>
              <input 
                type="password"
                name="ldap_bind_password" 
                value={config.ldap_bind_password} 
                onChange={handleChange} 
                className="input" 
                placeholder="********" 
              />
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="section-card">
            <h3 className="mb-6 font-semibold text-lg text-blue">Search & Logic</h3>
            <div className="space-y-6">
              <div className="form-group">
                <label>User Search Filter</label>
                <input 
                  name="ldap_user_filter" 
                  value={config.ldap_user_filter} 
                  onChange={handleChange} 
                  className="input" 
                  placeholder="(uid={{username}})" 
                />
              </div>
              <div className="flex items-center gap-3 py-2">
                <input 
                  id="ldap_enabled"
                  type="checkbox" 
                  name="ldap_enabled" 
                  checked={config.ldap_enabled === 'true'} 
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="ldap_enabled" className="font-bold cursor-pointer" style={{ marginBottom: 0 }}>Enable LDAP Authentication</label>
              </div>
              <div className="button-group">
                <button 
                  onClick={handleSave} 
                  className="btn btn-primary" 
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
                <button 
                  onClick={handleTestConnection} 
                  className="btn btn-secondary" 
                  disabled={testingConnection}
                >
                  {testingConnection ? 'Connecting...' : 'Test Connection'}
                </button>
              </div>
            </div>
          </div>

          <div className="section-card border-blue">
            <h3 className="mb-6 font-semibold text-lg text-blue">Diagnostics: Test Login</h3>
            <div className="space-y-6">
              <div className="form-group">
                <label>Test Username</label>
                <input 
                  placeholder="Enter username" 
                  className="input" 
                  value={testLogin.username}
                  onChange={e => setTestLogin({...testLogin, username: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Test Password</label>
                <input 
                  type="password" 
                  placeholder="Enter password" 
                  className="input" 
                  value={testLogin.password}
                  onChange={e => setTestLogin({...testLogin, password: e.target.value})}
                />
              </div>
              <button 
                onClick={handleTestLogin} 
                className="btn btn-outline w-full mt-2" 
                disabled={testingLogin}
                style={{ height: '42px' }}
              >
                {testingLogin ? 'Authenticating...' : 'Perform Test Authentication'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
